import { useMemo } from "react";
import { useCreateProductReviewMutation } from "@/lib/api/apiSlice";
import { getRtkQueryErrorMessage } from "@/lib/error";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { routes } from "@/lib/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { Star } from "lucide-react";
import {
  addProductReviewInputSchema,
  IAddProductReviewInput,
  IProduct,
  IProductReview,
} from "muminsmart-utils";
import { useSession } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";
import { ProductSectionTitle } from "../product/ProductDetails";

type ProductReviewFormValues = z.infer<typeof addProductReviewInputSchema>;

export interface ProductReviewProps {
  reviews: IProductReview[];
  reviewSummary: {
    rating: string;
    ratingsCount: string;
    reviewsCount: string;
  };
  product: IProduct;
}

interface ProductReviewFormProps {
  product: IProduct;
  className?: string;
  compact?: boolean;
  onSubmitted?: () => void;
}

export const ProductReviewForm = ({
  product,
  className,
  compact = false,
  onSubmitted,
}: ProductReviewFormProps) => {
  const navigate = useNavigate();
  const { status } = useSession();
  const [createProductReview] = useCreateProductReviewMutation();
  const form = useForm<
    IAddProductReviewInput,
    undefined,
    ProductReviewFormValues
  >({
    resolver: zodResolver(addProductReviewInputSchema),
    defaultValues: {
      productId: product.id,
      rating: 5,
      title: "",
      message: "",
    },
  });

  const handleSubmitReview = async (values: ProductReviewFormValues) => {
    if (status !== "authenticated") {
      toast.error("Please login to submit a review");
      navigate(routes.auth.login);
      return;
    }

    try {
      const data = await createProductReview({
        productId: product.id,
        rating: values.rating,
        title: values.title?.trim() || undefined,
        message: values.message?.trim() || undefined,
      }).unwrap();

      if (!data?.id) {
        toast.error("Failed to submit review");
        return;
      }

      form.reset({
        rating: 5,
        title: "",
        message: "",
      });
      toast.success("Review submitted successfully");
      onSubmitted?.();
    } catch (error: unknown) {
      toast.error(getRtkQueryErrorMessage(error, "Failed to submit review"));
    }
  };

  return (
    <div
      className={cn(
        "rounded-3xl border border-[#e7e4de] bg-white p-6 shadow-sm",
        compact && "rounded-xl p-2 shadow-none",
        className,
      )}
    >
      {!compact ? (
        <>
          <p className="text-lg font-semibold text-[#2f2a27]">Write a review</p>
          <p className="mt-2 text-sm text-[#6f6f6f]">
            Share your experience to help others choose the right product.
          </p>
        </>
      ) : null}

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmitReview)}
          className={cn("mt-4 space-y-4", compact && "mt-0 space-y-2")}
        >
          <FormField
            control={form.control}
            name="rating"
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  className={cn(
                    "block text-sm font-medium text-[#2f2a27]",
                    compact && "sr-only",
                  )}
                >
                  Rating
                </FormLabel>
                <FormControl>
                  <div
                    className={cn(
                      "flex items-center gap-2",
                      compact && "gap-0.5",
                    )}
                  >
                    {Array.from({ length: 5 }).map((_, index) => {
                      const value = index + 1;
                      return (
                        <Star
                          key={value}
                          size={compact ? 20 : 30}
                          className={cn(
                            Number(field.value ?? 0) >= value
                              ? "stroke-primary fill-primary"
                              : "bg-white text-gray-300",
                          )}
                          onClick={() => field.onChange(value)}
                        />
                      );
                    })}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  className={cn(
                    "block text-sm font-medium text-[#2f2a27]",
                    compact && "sr-only",
                  )}
                >
                  Title
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value}
                    className={cn(
                      "w-full rounded-3xl border border-[#e7e4de] bg-[#faf9f6] px-4 py-3 text-sm text-[#2f2a27] transition focus:border-primary",
                      compact && "h-8 rounded-xl px-3 py-1.5 text-xs",
                    )}
                    placeholder="Summarize your review"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  className={cn(
                    "block text-sm font-medium text-[#2f2a27]",
                    compact && "sr-only",
                  )}
                >
                  Review
                </FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value}
                    className={cn(
                      "h-28 w-full rounded-3xl border border-[#e7e4de] bg-[#faf9f6] px-4 py-3 text-sm text-[#2f2a27] transition focus:border-primary",
                      compact && "h-16 rounded-xl px-3 py-1.5 text-xs",
                    )}
                    placeholder={
                      compact
                        ? "Your review"
                        : "Tell us what you liked or what could be improved"
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className={cn(
              "w-full rounded-full bg-[#0d4e5b] px-4 py-3 text-base font-semibold text-white transition hover:bg-[#0d4e5b]",
              compact && "h-8 py-1.5 text-xs",
              form.formState.isSubmitting && "opacity-60",
            )}
          >
            {form.formState.isSubmitting ? "Submitting..." : "Submit Review"}
          </Button>
        </form>
      </Form>
    </div>
  );
};

const ProductReview = ({
  reviewSummary,
  reviews,
  product,
}: ProductReviewProps) => {
  const reviewBreakDown = useMemo(() => {
    let totalReviews = 0;
    const ratingCounts = new Map<number, number>();

    for (const review of reviews) {
      const ratingCount = ratingCounts.get(review.rating);
      if (ratingCount) {
        ratingCounts.set(review.rating, ratingCount + 1);
      } else {
        ratingCounts.set(review.rating, 1);
      }
      totalReviews++;
    }

    const REVIEW_BREAKDOWN = [
      { label: "Excellent", rating: 5, value: 0, width: "w-[20%]" },
      { label: "Very Good", rating: 4, value: 0, width: "w-[20%]" },
      { label: "Good", rating: 3, value: 0, width: "w-[30%]" },
      { label: "Average", rating: 2, value: 0, width: "w-[12%]" },
      { label: "Poor", rating: 1, value: 0, width: "w-[10%]" },
    ];

    REVIEW_BREAKDOWN.forEach((rb) => {
      const ratingCount = ratingCounts.get(rb.rating) ?? 0;
      const width = Math.round((ratingCount / totalReviews) * 100);
      rb.value = ratingCount;
      rb.width = `${width}%`;
    });

    return REVIEW_BREAKDOWN;
  }, [reviews]);

  return (
    <section className="bg-white px-4 py-5 pb-30 space-y-2">
      <ProductSectionTitle title="Reviews & Ratings" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <div className="flex items-start gap-1 text-[#101010]">
            <span className="text-6xl font-semibold leading-none">
              {reviewSummary.rating}
            </span>
            <Star className="size-5 fill-[#d18a32] text-[#d18a32]" />
          </div>
          <p className="text-md text-[#5f5f5f]">
            {reviewSummary.ratingsCount} Ratings,
            <br />
            {reviewSummary.reviewsCount} Reviews
          </p>
        </div>

        <div className="space-y-2">
          {reviewBreakDown.map((item) => (
            <div
              key={item.label}
              className="grid grid-cols-[70px_1fr_32px] items-center gap-3 text-sm text-[#5b5b5b]"
            >
              <p>{item.label}</p>
              <div className="h-0.75 rounded-full bg-[#dedede]">
                <div
                  className={"h-0.75 rounded-full bg-[#d18a32]"}
                  style={{
                    width: item.width,
                  }}
                />
              </div>
              <p className="text-right">{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 grid gap-6">
        <div className="space-y-4">
          {reviews.length ? (
            reviews.map((review) => (
              <div
                key={review.id}
                className="mt-5 border-t border-[#d8d8d8] pt-5"
              >
                <p className="text-lg text-[#5f6e7f]">
                  {review.user?.fullName ?? "Mumin's Mart user"}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[#5b5b5b]">
                  <span className="inline-flex items-center rounded-full bg-[#2da16f] px-2 py-0.5 text-sm font-semibold text-white">
                    {review.rating}
                    <Star className="ml-1 h-3 w-3 fill-white text-white" />
                  </span>
                  <span className="text-md">Posted on 9 Feb 2024</span>
                </div>

                <p className="mt-3 text-md leading-tight text-[#535353] font-semibold">
                  {review.title}
                </p>

                <p className="mt-3 text-md leading-tight text-[#535353]">
                  {review.message}
                </p>
              </div>
            ))
          ) : (
            <div className="rounded-3xl border border-[#e7e4de] bg-[#faf9f6] p-6 text-center text-sm text-[#5f5f5f]">
              No reviews yet. Be the first to review this product.
            </div>
          )}
          {/* {reviews.length > 3 ? (
              <div className="text-center">
                <button
                  type="button"
                  className={`mt-3 text-md font-medium text-[#be7f2f] underline underline-offset-4 ${inter.className}`}
                >
                  View All Reviews
                </button>
              </div>
            ) : null} */}
        </div>

        <ProductReviewForm product={product} />
      </div>
    </section>
  );
};

export default ProductReview;
